import { useState, useRef } from 'react'

const SLIDES = [
  {
    tag: '01 · Het probleem',
    emoji: '⚡',
    title: 'Jouw stroom.\nHun winst.',
    body: 'Je zonnepanelen leveren straks bijna niets meer op — de salderingsregeling verdwijnt in 2027. Ondertussen verdienen grote energiebedrijven grof aan jouw laadbeurt. Dat kan anders.',
    stats: ['📉 Saldering stopt 2027', '⚡ €0,65/kWh bij grote aanbieders'],
    bg: '#f7f4ee',
    accent: '#3d9e5f',
    dark: false,
  },
  {
    tag: '02 · De oplossing',
    emoji: '⚡',
    title: 'Laad bij je\nburen',
    body: 'Buurtlader verbindt EV-rijders met laadpalen bij mensen in de buurt. Goedkoper voor rijders. Extra inkomsten voor eigenaren. Minder druk op het stroomnet.',
    stats: ['💚 Tot 60% goedkoper', '🏠 €300+ per jaar verdienen'],
    bg: '#1a2e20',
    accent: '#5cb87a',
    dark: true,
  },
  {
    tag: '03 · Jij',
    emoji: '🙌',
    title: 'Samen maken\nwe het verschil',
    body: 'Buurtlader werkt alleen als de buurt meedoet. Meld je aan, word één van de eersten, en help mee aan eerlijker en groener laden in Nederland.',
    stats: ['🇳🇱 Gemaakt in Nederland', '🌱 Voor een groenere buurt'],
    bg: '#3d9e5f',
    accent: '#ffffff',
    dark: true,
  },
  {
    tag: '04 · Hoe werkt het?',
    emoji: null,
    title: 'Simpel.\nEerlijk. Lokaal.',
    steps: [
      { num: '1', title: '🔍 Rijder zoekt een paal', body: 'Zoek beschikbare laadpalen bij jou in de buurt — altijd goedkoper dan commerciële palen.' },
      { num: '2', title: '📅 Rijder boekt een tijdslot', body: 'Kies een tijd die past. De eigenaar bevestigt — geen gedoe, geen verrassingen.' },
      { num: '3', title: '⚡ Rijder laadt goedkoper', body: 'Tot 60% goedkoper dan publieke palen. Eerlijk geprijsd, lokaal opgewekt.' },
      { num: '4', title: '☀️ Eigenaar verdient meer', body: 'Verkoop je overproductie zonne-energie voor een eerlijke prijs.' },
    ],
    bg: '#1a1000',
    accent: '#fbbf24',
    dark: true,
  },
]

const OnboardingPage = ({ onDone }) => {
  const [current, setCurrent] = useState(0)
  const touchStart = useRef(null)
  const slide = SLIDES[current]
  const isLast = current === SLIDES.length - 1

  const next = () => isLast ? onDone() : setCurrent(p => p + 1)
  const prev = () => current > 0 && setCurrent(p => p - 1)

  return (
    <div
      onTouchStart={e => { touchStart.current = e.touches[0].clientX }}
      onTouchEnd={e => {
        if (!touchStart.current) return
        const d = touchStart.current - e.changedTouches[0].clientX
        if (d > 50) next()
        else if (d < -50) prev()
        touchStart.current = null
      }}
      style={{
        height: '100vh',
        background: slide.bg,
        display: 'flex',
        flexDirection: 'column',
        transition: 'background 0.4s ease',
        fontFamily: 'DM Sans, sans-serif',
        overflow: 'hidden'
      }}>

      {/* Header */}
      <div style={{
        padding: '48px 24px 0',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <div style={{
          display: 'flex', alignItems: 'center', gap: 8
        }}>
          <div style={{
            width: 8, height: 8, borderRadius: '50%',
            background: slide.accent
          }} />
          <span style={{
            fontFamily: 'Fraunces, serif', fontWeight: 900,
            fontSize: 18, color: slide.accent
          }}>
            Buurtlader
          </span>
        </div>
        <button
          onClick={onDone}
          style={{
            background: 'transparent',
            border: `1px solid ${slide.accent}40`,
            borderRadius: 20, padding: '6px 16px',
            color: `${slide.accent}99`, fontSize: 12,
            fontWeight: 600, cursor: 'pointer',
            fontFamily: 'DM Sans, sans-serif'
          }}>
          Overslaan
        </button>
      </div>

      {/* Content */}
      <div style={{ flex: 1, padding: '32px 28px 0', overflowY: 'auto' }}>

        {slide.tag && (
          <div style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            background: `${slide.accent}20`,
            border: `1px solid ${slide.accent}40`,
            borderRadius: 20, padding: '5px 12px',
            fontSize: 11, color: slide.accent,
            fontWeight: 800, marginBottom: 20,
            letterSpacing: 0.5, textTransform: 'uppercase'
          }}>
            {slide.tag}
          </div>
        )}

        {slide.emoji && (
          <div style={{ fontSize: 56, marginBottom: 16, lineHeight: 1 }}>
            {slide.emoji}
          </div>
        )}

        <div style={{
          fontFamily: 'Fraunces, serif',
          fontSize: 36, fontWeight: 900,
          color: slide.dark ? 'white' : '#1a2e20',
          lineHeight: 1.1, marginBottom: 16,
          whiteSpace: 'pre-line'
        }}>
          {slide.title}
        </div>

        {slide.body && (
          <p style={{
            color: slide.dark ? 'rgba(255,255,255,0.75)' : '#3d5a45',
            fontSize: 15, lineHeight: 1.7, marginBottom: 24
          }}>
            {slide.body}
          </p>
        )}

        {/* Stats pills */}
        {slide.stats && (
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 24 }}>
            {slide.stats.map((s, i) => (
              <div key={i} style={{
                background: `${slide.accent}20`,
                border: `1px solid ${slide.accent}40`,
                borderRadius: 100, padding: '7px 14px',
                fontSize: 12, fontWeight: 600,
                color: slide.dark ? 'rgba(255,255,255,0.85)' : '#3d5a45'
              }}>
                {s}
              </div>
            ))}
          </div>
        )}

        {/* Steps */}
        {slide.steps && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
            {slide.steps.map((step, i) => (
              <div key={i} style={{ display: 'flex', gap: 14, marginBottom: 4 }}>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0 }}>
                  <div style={{
                    width: 28, height: 28, borderRadius: '50%',
                    background: slide.accent, color: '#1a1000',
                    fontWeight: 900, fontSize: 13,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontFamily: 'Fraunces, serif', flexShrink: 0
                  }}>
                    {step.num}
                  </div>
                  {i < slide.steps.length - 1 && (
                    <div style={{ width: 2, flex: 1, background: 'rgba(255,255,255,0.15)', margin: '4px 0', minHeight: 12 }} />
                  )}
                </div>
                <div style={{ paddingBottom: 14 }}>
                  <div style={{ fontWeight: 700, fontSize: 13, color: slide.accent, marginBottom: 3 }}>
                    {step.title}
                  </div>
                  <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.65)', lineHeight: 1.5 }}>
                    {step.body}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Bottom */}
      <div style={{ padding: '20px 28px 44px' }}>
        {/* Dots */}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'center', marginBottom: 20 }}>
          {SLIDES.map((_, i) => (
            <div
              key={i}
              onClick={() => setCurrent(i)}
              style={{
                height: 6,
                width: i === current ? 24 : 6,
                borderRadius: 3,
                background: i === current ? slide.accent : `${slide.accent}30`,
                transition: 'all 0.3s ease',
                cursor: 'pointer'
              }}
            />
          ))}
        </div>

        <button
          onClick={next}
          style={{
            width: '100%',
            background: slide.accent,
            border: 'none', borderRadius: 18,
            padding: '17px',
            color: slide.dark && current !== 2 ? '#1a2e20' : 'white',
            fontSize: 16, fontWeight: 900,
            cursor: 'pointer',
            fontFamily: 'Fraunces, serif'
          }}>
          {isLast ? 'Bekijk de kaart →' : 'Volgende →'}
        </button>

        {current === 0 && (
          <div style={{
            textAlign: 'center', marginTop: 14,
            color: `${slide.accent}60`, fontSize: 13
          }}>
            Al een account?{' '}
            <span
              onClick={onDone}
              style={{ color: slide.accent, fontWeight: 700, cursor: 'pointer' }}>
              Inloggen
            </span>
          </div>
        )}
      </div>
    </div>
  )
}

export default OnboardingPage
